import type {
  CSSProperties,
  FC,
  RefObject,
  ReactNode,
  TouchEventHandler,
} from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { DrawerActions } from './DrawerActions';
import { Overlay } from '@mantine/core';
import { DrawerState } from './consts';
import classes from './Drawer.module.css';

const MIN_SWIPE_DISTANCE = 50;
const PREVIEW_HEIGHT = 150;

type TouchState = {
  swiping: boolean;
  startY?: number;
  thumbY?: number;
};

interface Props {
  id?: string;
  header?: ReactNode;
  /** Render prop alternative to `header` that receives drawer state and a toggle callback. */
  renderHeader?: (
    drawerState: DrawerState,
    toggleDrawer: () => void
  ) => ReactNode;
  showAll?: boolean;
  scrollingContentRef?: RefObject<HTMLDivElement | null> | null;
  onClose?: () => void;
  hasTabs?: boolean;
  children?: React.ReactNode;
  height?: string;
  hasPreview?: boolean;
  /** Height in px of the drawer when in preview (collapsed) state. Defaults to 150. */
  previewHeight?: number;
  offsetTop?: number;
  setOffsetTop?: (a: number) => void;
}

export const Drawer: FC<Props> = ({
  id = '',
  header,
  renderHeader,
  showAll = false,
  onClose = () => {},
  scrollingContentRef,
  hasTabs = false,
  offsetTop,
  setOffsetTop = () => {},
  ...props
}) => {
  const [touchState, setTouchState] = useState<TouchState>({
    swiping: false,
  });
  const isSmallScreen = useSmallScreen();
  const drawerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [drawerState, setDrawerState] = useState<DrawerState>(
    showAll ? DrawerState.open : DrawerState.preview
  );

  // Allow preview when there's a header unless `hasPreview` prop has been defined
  const hasPreview =
    (props.hasPreview === undefined &&
      (header !== undefined || renderHeader !== undefined)) ||
    (props.hasPreview ?? false);

  const previewHeight = props.previewHeight ?? PREVIEW_HEIGHT;
  const drawerBody = document.getElementById('drawer-body');

  useEffect(() => {
    // Scroll to the top of the modal when a new element is loaded
    bodyRef.current?.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (drawerBody) {
      const onScroll = () => setOffsetTop(drawerBody.scrollTop);
      // clean up code
      drawerBody.removeEventListener('scroll', onScroll);
      drawerBody.addEventListener('scroll', onScroll, { passive: true });
      return () => drawerBody.removeEventListener('scroll', onScroll);
    }
  }, [drawerBody, setOffsetTop]);

  /**
   * Drawer is only swipeable on small screen and if any scrollable content is at the top
   */
  const getIsSwipeable = () => {
    if (!isSmallScreen) {
      return false;
    }

    if (offsetTop) {
      return offsetTop === 0;
    }

    return (
      scrollingContentRef === undefined ||
      (scrollingContentRef?.current !== null &&
        scrollingContentRef?.current.scrollTop === 0)
    );
  };

  const onTouchStart: TouchEventHandler<HTMLDivElement> = (e) => {
    if (drawerRef.current && isSmallScreen && e.touches && e.touches.length) {
      const startY = e.touches[0].clientY;
      if (drawerRef.current) {
        if (drawerState === DrawerState.preview) {
          drawerRef.current.style.height = props.height ?? '570px';
          const previewHeight = drawerRef.current.clientHeight - PREVIEW_HEIGHT;
          drawerRef.current.style.transform = `translateY(${previewHeight}px)`;
        }
      }
      setTouchState((p: TouchState) => {
        return { ...p, startY, thumbY: startY };
      });
    }
  };

  const onTouchMove: TouchEventHandler<HTMLDivElement> = (e) => {
    if (getIsSwipeable() && e.changedTouches && e.changedTouches.length) {
      setTouchState((p: TouchState) => {
        const thumbY = e.touches[0].clientY;
        if (drawerRef.current && p.startY) {
          if (drawerState === DrawerState.preview) {
            const previewHeight =
              drawerRef.current.clientHeight - PREVIEW_HEIGHT;
            const offset = Math.min(p.startY - thumbY, previewHeight);
            drawerRef.current.style.transform = `translateY(${Math.round(
              previewHeight - offset
            )}px)`;
          } else if (drawerState === DrawerState.open) {
            const offset = Math.min(p.startY - thumbY, 0);
            drawerRef.current.style.transform = `translateY(${Math.round(
              offset * -1
            )}px)`;
          }
        }

        return { ...p, thumbY, swiping: true };
      });
    }
  };

  const setDrawerPosition = useCallback(
    (state: DrawerState) => {
      if (drawerRef.current) {
        drawerRef.current.style.transform = 'translate(0)';
        if (state === DrawerState.preview) {
          drawerRef.current.style.height = `${previewHeight}px`;
        } else if (state === DrawerState.open) {
          drawerRef.current.style.height = props.height ?? '570px';
        }
      }
    },
    [props.height, previewHeight]
  );

  const onTouchEnd: TouchEventHandler<HTMLDivElement> = (e) => {
    if (
      getIsSwipeable() &&
      e.changedTouches &&
      e.changedTouches.length &&
      touchState.startY
    ) {
      const changedY = touchState.startY - e.changedTouches[0].clientY;
      if (!touchState.swiping || Math.abs(changedY) < MIN_SWIPE_DISTANCE) {
        // reset drawer position - didn't swipe far enough
        setDrawerPosition(drawerState);
        return;
      }

      setDrawerState((p) => {
        let state;
        // swiped to hide
        if (changedY * -1 > MIN_SWIPE_DISTANCE) {
          // if an open drawer is swiped a little and there is a preview, show the preview
          if (
            hasPreview &&
            drawerRef.current &&
            Math.abs(changedY) < drawerRef.current.clientHeight / 2 &&
            p === DrawerState.open
          ) {
            state = DrawerState.preview;
          } else {
            onClose();
            state = DrawerState.closed;
          }
        } else {
          // swiped to show
          state = DrawerState.open;
        }

        setDrawerPosition(state);
        return state;
      });
    }
    setTouchState({ swiping: false });
  };

  const handleDrawerStateChange = (newDrawerState: DrawerState) => {
    setDrawerPosition(newDrawerState);
    setDrawerState(newDrawerState);
  };

  /** Toggle between preview ↔ open states */
  const toggleDrawer = () => {
    handleDrawerStateChange(
      drawerState === DrawerState.preview
        ? DrawerState.open
        : DrawerState.preview
    );
  };

  // Resolve header content: prefer renderHeader (receives drawer state) over static header
  const resolvedHeader = renderHeader ?? (header ? () => header : undefined);

  // Drive the mobile preview height via a CSS custom property so the dynamic
  // `previewHeight` prop flows into the @media-scoped rule in Drawer.module.css.
  const wrapperStyle = {
    '--drawer-preview-height': `${previewHeight}px`,
  } as CSSProperties;

  return drawerState === DrawerState.closed ? null : (
    <>
      {drawerState === DrawerState.open && !isSmallScreen && (
        <Overlay zIndex={0} fixed={true} />
      )}
      <div
        id={id}
        ref={drawerRef}
        data-testid="drawer"
        className={`${classes.wrapper} ${!showAll ? classes.wrapperPreview : ''}`}
        style={wrapperStyle}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <DrawerActions
          hasPreview={hasPreview}
          closeDrawer={() => {
            onClose();
            handleDrawerStateChange(DrawerState.closed);
          }}
        />
        {/* eslint-disable react-hooks/refs -- toggleDrawer is a plain callback, not a ref */}
        {resolvedHeader && (
          <div id="drawer-header" className={classes.header}>
            {resolvedHeader(drawerState, toggleDrawer)}
          </div>
        )}
        {/* eslint-enable react-hooks/refs */}
        <div
          id="drawer-body"
          data-testid="drawer-body"
          className={`${classes.body} ${hasTabs ? classes.bodyHasTabs : ''}`}
          ref={bodyRef}
        >
          {props.children}
        </div>
      </div>
    </>
  );
};
